import Phaser from 'phaser';
import { Boss, BossType } from '../entities/Boss';
import { RENDER_LAYERS } from '../systems/RenderSystem';
import { DESIGN_WIDTH, COLORS } from '../constants';
import { MonsterMovementType } from '../entities/Monster';

// ============================================
// Boss Health Bar UI
// Based on tower_leveling_and_bosses.md
// ============================================

const BOSS_NAMES: Record<BossType, string> = {
  brute_boss: 'Brute Boss',
  bronze_idol: 'Bronze Idol',
  earth_tower: 'Earth Tower',
  snail_beast: 'Snail Beast',
  giant_bug: 'Giant Bug',
};

export class BossHealthBar extends Phaser.GameObjects.Container {
  private boss: Boss | null = null;
  private background: Phaser.GameObjects.Rectangle;
  private healthBar: Phaser.GameObjects.Rectangle;
  private healthBarBg: Phaser.GameObjects.Rectangle;
  private nameText: Phaser.GameObjects.Text;
  private phaseText: Phaser.GameObjects.Text;
  private hpText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, y: number = 80) {
    super(scene, DESIGN_WIDTH / 2, y);

    // Background panel
    this.background = scene.add.rectangle(0, 0, 400, 50, 0x1a1a2e, 0.9);
    this.background.setStrokeStyle(3, 0x9400d3);

    // Health bar background
    this.healthBarBg = scene.add.rectangle(0, 8, 360, 18, 0x000000);

    // Health bar (purple for bosses)
    this.healthBar = scene.add.rectangle(-180, 8, 360, 16, 0x9400d3);
    this.healthBar.setOrigin(0, 0.5);

    // Boss name
    this.nameText = scene.add.text(0, -12, 'BOSS', {
      fontSize: '14px',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    this.nameText.setOrigin(0.5);

    // Phase indicator
    this.phaseText = scene.add.text(170, -12, '', {
      fontSize: '12px',
      color: '#ff88ff',
    });
    this.phaseText.setOrigin(1, 0.5);

    // HP text
    this.hpText = scene.add.text(0, 8, '', {
      fontSize: '12px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    this.hpText.setOrigin(0.5);

    this.add([this.background, this.healthBarBg, this.healthBar, this.nameText, this.phaseText, this.hpText]);

    this.setScrollFactor(0);
    this.setDepth(RENDER_LAYERS.UI_SCREEN + 50);
    this.setVisible(false);

    scene.add.existing(this);
  }

  /**
   * Show boss health bar for a boss
   */
  show(boss: Boss): void {
    this.boss = boss;
    this.setVisible(true);

    // Set boss name
    const name = BOSS_NAMES[boss.bossType] || boss.bossType;
    this.nameText.setText(`BOSS: ${name}`);

    // Update phase indicator
    this.updatePhase(boss.getCurrentPhase(), boss.getMovementType());

    // Subscribe to phase changes
    boss.onPhaseChange = (phase, movementType) => {
      this.updatePhase(phase, movementType);
    };

    // Animate in
    this.y = -50;
    this.scene.tweens.add({
      targets: this,
      y: 80,
      duration: 500,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Hide boss health bar
   */
  hide(): void {
    this.scene.tweens.add({
      targets: this,
      y: -50,
      duration: 300,
      onComplete: () => {
        this.setVisible(false);
        this.boss = null;
      },
    });
  }

  /**
   * Update the health bar (call each frame while boss is active)
   */
  update(): void {
    if (!this.boss || !this.visible) return;

    const hp = this.boss.getHp();
    const maxHp = this.boss.getMaxHp();
    const percent = Math.max(0, hp / maxHp);

    // Update health bar width
    this.healthBar.setSize(360 * percent, 16);

    // Update HP text
    this.hpText.setText(`${Math.ceil(hp)} / ${maxHp}`);

    // Color based on HP
    if (percent > 0.5) {
      this.healthBar.setFillStyle(0x9400d3);  // Purple
    } else if (percent > 0.25) {
      this.healthBar.setFillStyle(0xda70d6);  // Orchid
    } else {
      this.healthBar.setFillStyle(0xff1493);  // Deep pink (critical)
    }

    // Hide if boss is dead
    if (this.boss.isDead()) {
      this.hide();
    }
  }

  /**
   * Update phase indicator (hidden - no phase text shown)
   */
  private updatePhase(_phase: number, _movementType: MonsterMovementType): void {
    this.phaseText.setText('');
  }
}
